import React, { useState, useEffect, useRef } from "react";
import { Box, IconButton, Tooltip, Popper, Paper } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PaletteIcon from "@mui/icons-material/Palette";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useReactFlow } from "@xyflow/react";

const colors = ["#444", "#6B5A36", "#4A342E", "#7C2D2D", "#1F4A3F", "#2D3652"];

export const NoteNode = ({ id, data, selected }) => {
  const { setNodes, getNode } = useReactFlow();
  const nodeRef = useRef(null);
  const [hovering, setHovering] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editing, setEditing] = useState(false);

  const [noteData, setNoteData] = useState({
    title: data.title || "I'm a note",
    body:
      data.body ||
      `## I'm a note\n\n**Double click** to edit me. [Guide](https://docs.n8n.io/workflows/sticky-notes/)`,
    color: data.color || colors[0],
  });

  const [size, setSize] = useState({
    width: data.width || 320,
    height: data.height || 220,
  });

  useEffect(() => {
    setNodes((nodes) =>
      nodes.map((n) =>
        n.id === id
          ? {
              ...n,
              data: {
                ...n.data,
                ...noteData,
                width: size.width,
                height: size.height,
              },
            }
          : n
      )
    );
  }, [noteData, size, id, setNodes]);

  const handleDelete = () => {
    setNodes((nodes) => nodes.filter((n) => n.id !== id));
    if (data.onDelete) data.onDelete(id);
  };

  const handleColorClick = (e) => {
    e.stopPropagation();
    setAnchorEl(anchorEl ? null : e.currentTarget);
  };

  const handleColorSelect = (color) => {
    setNoteData((prev) => ({ ...prev, color }));
    setAnchorEl(null);
  };

  const startResize = (e, direction) => {
    e.stopPropagation();
    e.preventDefault();

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;

    const node = getNode(id);
    const startPos = node?.position || { x: 0, y: 0 };

    const onMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startPos.x;
      let newY = startPos.y;

      if (direction.includes("e")) newWidth = Math.max(160, startWidth + dx);
      if (direction.includes("s")) newHeight = Math.max(120, startHeight + dy);
      if (direction.includes("w")) {
        const w = Math.max(160, startWidth - dx);
        newX = startPos.x + (startWidth - w);
        newWidth = w;
      }
      if (direction.includes("n")) {
        const h = Math.max(120, startHeight - dy);
        newY = startPos.y + (startHeight - h);
        newHeight = h;
      }

      setSize({ width: newWidth, height: newHeight });

      setNodes((nodes) =>
        nodes.map((n) =>
          n.id === id
            ? {
                ...n,
                position: { x: newX, y: newY },
              }
            : n
        )
      );
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  return (
    <Box
      ref={nodeRef}
      sx={{
        width: size.width,
        height: size.height,
        background: noteData.color,
        border: selected ? "3px solid #aaa" : editing ? "3px solid #f39c12" : "2px solid #666",
        borderRadius: 2,
        fontFamily: "monospace",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        cursor: editing ? "text" : "grab",
        zIndex: 0,
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => {
        setHovering(false);
        setAnchorEl(null);
      }}
      onDoubleClick={() => setEditing(true)}
      onMouseDown={(e) => {
        if (editing) e.stopPropagation();
      }}
    >
      {/* Toolbar */}
      {hovering && (
        <Box
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            zIndex: 3,
            display: "flex",
            gap: 1,
            backgroundColor: "#333333",
            borderRadius: 1,
            padding: "2px 6px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
          }}
        >
          <Tooltip title="Change Color">
            <IconButton size="small" onClick={handleColorClick}>
              <PaletteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={handleDelete}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Editor */}
      {editing ? (
        <Box
          component="textarea"
          value={noteData.body}
          onChange={(e) => setNoteData((prev) => ({ ...prev, body: e.target.value }))}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              setEditing(false);
            }
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          autoFocus
          sx={{
            flex: 1,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.15)",
            border: "none",
            outline: "none",
            resize: "none",
            fontFamily: "monospace",
            fontSize: 13,
            color: "#fff",
            padding: 1,
            overflowY: "auto",
            borderRadius: 1,
            lineHeight: 1.5,
          }}
        />
      ) : (
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            color: "#fff",
            fontSize: 13,
            cursor: "text",
            px: 2,
            py: 1,
          }}
          onDoubleClick={() => setEditing(true)}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{noteData.body}</ReactMarkdown>
        </Box>
      )}

      {/* Resize handlers */}
      {["n", "s", "e", "w", "ne", "nw", "se", "sw"].map((dir) => (
        <Box
          key={dir}
          className="nodrag"
          onMouseDown={(e) => startResize(e, dir)}
          sx={{
            position: "absolute",
            zIndex: 5,
            backgroundColor: "transparent", // can change to rgba(255,255,255,0.05) if you want
            ...(dir === "n" && {
              top: -6,
              left: 0,
              right: 0,
              height: 16,
              cursor: "ns-resize",
            }),
            ...(dir === "s" && {
              bottom: -6,
              left: 0,
              right: 0,
              height: 16,
              cursor: "ns-resize",
            }),
            ...(dir === "e" && {
              right: -6,
              top: 0,
              bottom: 0,
              width: 16,
              cursor: "ew-resize",
            }),
            ...(dir === "w" && {
              left: -6,
              top: 0,
              bottom: 0,
              width: 16,
              cursor: "ew-resize",
            }),
            ...(dir === "ne" && {
              top: -6,
              right: -6,
              width: 16,
              height: 16,
              cursor: "nesw-resize",
            }),
            ...(dir === "nw" && {
              top: -6,
              left: -6,
              width: 16,
              height: 16,
              cursor: "nwse-resize",
            }),
            ...(dir === "se" && {
              bottom: -6,
              right: -6,
              width: 16,
              height: 16,
              cursor: "nwse-resize",
            }),
            ...(dir === "sw" && {
              bottom: -6,
              left: -6,
              width: 16,
              height: 16,
              cursor: "nesw-resize",
            }),
          }}
        />
      ))}

      {/* Color Picker */}
      <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} placement="top">
        <Paper
          elevation={3}
          sx={{
            px: 1.5,
            py: 1,
            display: "flex",
            gap: 1,
            alignItems: "center",
            borderRadius: 1,
          }}
        >
          {colors.map((color) => (
            <Box
              key={color}
              onClick={() => handleColorSelect(color)}
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: color,
                border: noteData.color === color ? "2px solid #222" : "1px solid #aaa",
                cursor: "pointer",
              }}
            />
          ))}
        </Paper>
      </Popper>
    </Box>
  );
};
